import type { ListingRequestListFilters } from '../types/listingRequestTypes'

// Query-keys factory for the listing-request module. The create mutation
// invalidates `lists()`; future seller-action mutations (Phase B) invalidate
// `detail(id)` + `lists()`.
export const listingRequestKeys = {
  all: ['listingRequest'] as const,
  lists: () => [...listingRequestKeys.all, 'list'] as const,
  // `page` is intentionally NOT part of the key — useInfiniteQuery manages
  // pagination via pageParam.
  list: (filters: ListingRequestListFilters) => [...listingRequestKeys.lists(), filters] as const,
  // Numbered-page variant (Sell Request table). Its own segment so the plain
  // useQuery cache never collides with the infinite query's page array, while
  // `lists()` invalidation still reaches both.
  page: (filters: ListingRequestListFilters, page: number) =>
    [...listingRequestKeys.lists(), 'page', filters, page] as const,
  // Under `lists()` so every mutation that already refreshes the lists (create,
  // revise, withdraw, respond to price) refreshes the KPI counts with them.
  statusCounts: () => [...listingRequestKeys.lists(), 'statusCounts'] as const,
  // Under `lists()` for the same reason: anything that changes a request's price
  // or status changes what the seller can expect to be paid.
  payoutSummary: () => [...listingRequestKeys.lists(), 'payoutSummary'] as const,
  details: () => [...listingRequestKeys.all, 'detail'] as const,
  detail: (id: string) => [...listingRequestKeys.details(), id] as const,
  timeline: (id: string) => [...listingRequestKeys.detail(id), 'timeline'] as const,
  agreements: (id: string) => [...listingRequestKeys.detail(id), 'agreements'] as const,
  // Not under `detail` — a quote belongs to a price, not to a saved request, and
  // the wizard asks for one before any request exists. `feeQuotes()` is the
  // handle for invalidating every quote at once, which is what an admin editing
  // a fee definition would need.
  feeQuotes: () => [...listingRequestKeys.all, 'feeQuote'] as const,
  feeQuote: (askingPrice: string, totalAreaSqm: string, builtUpAreaSqm: string) =>
    [...listingRequestKeys.feeQuotes(), askingPrice, totalAreaSqm, builtUpAreaSqm] as const,
}
